import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRICING_COPY = {
  fr: {
    eyebrow: "Tarifs",
    title: "Choisissez votre formule",
    subtitle: "Des analyses professionnelles pour améliorer vos performances sur le terrain.",
    popular: "Populaire",
    redirecting: "Redirection...",
    buy: "Acheter",
    subscribe: "S'abonner",
    paymentError: "Impossible d'ouvrir le paiement",
    faqTitle: "Questions fréquentes",
    packages: [
      {
        id: "collective",
        name: "Match Collectif",
        description: "Analyse tactique de votre équipe",
        price: 100,
        unit: "/ match",
        features: [
          "Analyse tactique complète",
          "Phases offensives et défensives",
          "Points forts de l'équipe",
          "Axes d'amélioration",
          "Livraison sous 4 jours",
        ],
        popular: false,
      },
      {
        id: "individual",
        name: "Match Individuel",
        description: "Analyse de vos performances personnelles",
        price: 100,
        unit: "/ match",
        features: [
          "Focus sur votre jeu",
          "Analyse de vos mouvements",
          "Points techniques à travailler",
          "Conseils personnalisés",
          "Livraison sous 4 jours",
        ],
        popular: false,
      },
      {
        id: "subscription",
        name: "Abonnement Mensuel",
        description: "Pour un suivi régulier",
        price: 300,
        unit: "/ mois",
        features: [
          "4 analyses par mois",
          "Collectif ou individuel au choix",
          "Support prioritaire WhatsApp",
          "Suivi de progression",
          "Résiliable à tout moment",
        ],
        popular: true,
      },
    ],
    faq: [
      {
        id: "faq-video",
        q: "Comment envoyer ma vidéo ?",
        a: "Utilisez SwissTransfer pour uploader votre vidéo et partagez le lien dans le formulaire de commande.",
      },
      {
        id: "faq-delai",
        q: "Quel délai de livraison ?",
        a: "Votre analyse est livrée sous 4 jours maximum après validation de votre commande.",
      },
      {
        id: "faq-annulation",
        q: "Puis-je annuler mon abonnement ?",
        a: "Oui, vous pouvez résilier votre abonnement à tout moment. Vos crédits restants seront utilisables jusqu'à la fin du mois.",
      },
      {
        id: "faq-format",
        q: "Quel format pour l'analyse ?",
        a: "L'analyse vous est envoyée sous forme de vidéo annotée avec commentaires et conseils.",
      },
    ],
  },
  en: {
    eyebrow: "Pricing",
    title: "Choose your plan",
    subtitle: "Professional analysis to improve your performance on the pitch.",
    popular: "Popular",
    redirecting: "Redirecting...",
    buy: "Buy",
    subscribe: "Subscribe",
    paymentError: "Unable to open payment",
    faqTitle: "Frequently asked questions",
    packages: [
      {
        id: "collective",
        name: "Team Match",
        description: "Tactical analysis for your team",
        price: 100,
        unit: "/ match",
        features: [
          "Complete tactical analysis",
          "Attacking and defensive phases",
          "Team strengths",
          "Improvement areas",
          "Delivery within 4 days",
        ],
        popular: false,
      },
      {
        id: "individual",
        name: "Individual Match",
        description: "Analysis of your personal performance",
        price: 100,
        unit: "/ match",
        features: [
          "Focus on your game",
          "Movement analysis",
          "Technical points to improve",
          "Personalized advice",
          "Delivery within 4 days",
        ],
        popular: false,
      },
      {
        id: "subscription",
        name: "Monthly Subscription",
        description: "For regular follow-up",
        price: 300,
        unit: "/ month",
        features: [
          "4 analyses per month",
          "Team or individual analysis",
          "Priority WhatsApp support",
          "Progress tracking",
          "Cancel anytime",
        ],
        popular: true,
      },
    ],
    faq: [
      {
        id: "faq-video",
        q: "How do I send my video?",
        a: "Use SwissTransfer to upload your video and share the link in the order form.",
      },
      {
        id: "faq-delai",
        q: "What is the delivery time?",
        a: "Your analysis is delivered within 4 days maximum after your order is confirmed.",
      },
      {
        id: "faq-annulation",
        q: "Can I cancel my subscription?",
        a: "Yes, you can cancel your subscription anytime. Your remaining credits stay available until the end of the month.",
      },
      {
        id: "faq-format",
        q: "What format is the analysis?",
        a: "Your analysis is delivered as an annotated video with comments and recommendations.",
      },
    ],
  },
};

const PricingPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loadingPackage, setLoadingPackage] = useState(null);
  const copy = PRICING_COPY[language] || PRICING_COPY.fr;

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate("/register");
      return;
    }

    setLoadingPackage(packageId);

    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });

      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || copy.paymentError);
      setLoadingPackage(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">{copy.eyebrow}</span>
            <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 text-white">
              {copy.title}
            </h1>
            <p className="text-zinc-500 mt-4 max-w-lg mx-auto">
              {copy.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {copy.packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`bg-[#18181B] p-8 relative ${
                  pkg.popular
                    ? "border-2 border-[#CCFF00] shadow-[0_0_20px_rgba(204,255,0,0.2)]"
                    : "border border-white/10"
                }`}
                data-testid={`pricing-${pkg.id}`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CCFF00] text-black text-xs font-bold uppercase px-3 py-1">
                    {copy.popular}
                  </span>
                )}

                <h2 className="text-xl font-bold uppercase text-white mb-2">{pkg.name}</h2>
                <p className="text-zinc-500 text-sm mb-6">{pkg.description}</p>

                <div className="mb-6">
                  <span className="text-5xl font-black text-white">{pkg.price}€</span>
                  <span className="text-zinc-500 ml-2">{pkg.unit}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={`${pkg.id}-${feature}`} className="flex items-center gap-2 text-sm text-zinc-400">
                      <CheckCircle className="w-4 h-4 text-[#CCFF00] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={loadingPackage !== null}
                  className={`w-full py-3 font-bold uppercase text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? "bg-[#CCFF00] text-black hover:bg-[#AACC00]"
                      : "border border-white/20 text-white hover:border-white hover:bg-white/5"
                  }`}
                  data-testid={`buy-${pkg.id}`}
                >
                  {loadingPackage === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {copy.redirecting}
                    </>
                  ) : (
                    pkg.id === "subscription" ? copy.subscribe : copy.buy
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-24">
            <h2 className="text-2xl font-bold uppercase text-white text-center mb-8">
              {copy.faqTitle}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {copy.faq.map((item) => (
                <div key={item.id} className="bg-[#18181B] border border-white/10 p-6">
                  <h3 className="text-white font-bold mb-2">{item.q}</h3>
                  <p className="text-zinc-500 text-sm">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
